import { useEffect, useMemo, useState, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Command } from 'cmdk'
import { useQuery } from '@tanstack/react-query'
import {
  Search,
  Package,
  Users,
  ShoppingCart,
  Truck,
  CalendarDays,
  Tag,
  BookOpen,
  LayoutDashboard,
  ArrowRight,
} from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { api } from '../lib/api'
import { searchDocs, canSeeSection, type Doc } from '../lib/docs'

// ── Page index ─────────────────────────────────────────────────────────────
// One row per admin route. Roles match the sidebar gating in Layout.tsx so
// the palette only suggests pages the user can actually open. Keywords help
// match queries that aren't the literal page label (e.g. "refund" → Orders,
// "ban customer" → Customers).
type AdminRole = 'super_admin' | 'operations' | 'accounting' | 'marketing'
type PageEntry = { path: string; label: string; group: string; roles: AdminRole[]; keywords: string }

const ALL: AdminRole[] = ['super_admin', 'operations', 'accounting', 'marketing']
const OPS: AdminRole[] = ['super_admin', 'operations']
const OPS_ACCT: AdminRole[] = ['super_admin', 'operations', 'accounting']
const OPS_MKT: AdminRole[] = ['super_admin', 'operations', 'marketing']
const SUP: AdminRole[] = ['super_admin']

const PAGES: PageEntry[] = [
  { path: '/dashboard',       label: 'Dashboard',         group: 'Insights',   roles: ALL,      keywords: 'home overview kpi stats' },
  { path: '/sales',           label: 'Sales',             group: 'Insights',   roles: OPS_ACCT, keywords: 'revenue analytics finance reports' },
  { path: '/customer-trends', label: 'Customer Trends',   group: 'Insights',   roles: ALL,      keywords: 'cohort retention ltv' },
  { path: '/abandoned-carts', label: 'Abandoned Carts',   group: 'Insights',   roles: OPS_MKT,  keywords: 'recovery left cart unfinished' },
  { path: '/performance',     label: 'Performance',       group: 'Insights',   roles: OPS_ACCT, keywords: 'driver leaderboard ratings on-time payouts' },
  { path: '/games-analytics', label: 'Games Analytics',   group: 'Insights',   roles: OPS_MKT,  keywords: 'prizes plays liability drops' },
  { path: '/ai-logs',         label: 'DrinX AI Logs',     group: 'Insights',   roles: SUP,      keywords: 'concierge llm cost cache' },
  { path: '/orders',          label: 'Orders',            group: 'Commerce',   roles: OPS_ACCT, keywords: 'refund cancel status delivery customer' },
  { path: '/products',        label: 'Products',          group: 'Commerce',   roles: OPS,      keywords: 'catalogue catalog inventory sku variants' },
  { path: '/categories',      label: 'Categories',        group: 'Commerce',   roles: OPS,      keywords: 'taxonomy parent slug' },
  { path: '/addons',          label: 'Add-ons',           group: 'Commerce',   roles: OPS,      keywords: 'ice mixers modifiers extras' },
  { path: '/promotions',      label: 'Promotions',        group: 'Commerce',   roles: OPS_MKT,  keywords: 'banners popups campaigns marketing' },
  { path: '/recommendations', label: 'Recommendations',   group: 'Commerce',   roles: OPS_MKT,  keywords: 'shelves picks suggested similar' },
  { path: '/discount-codes',  label: 'Discount Codes',    group: 'Commerce',   roles: OPS_MKT,  keywords: 'promo coupon code discount voucher' },
  { path: '/gift-cards',      label: 'Gift Cards',        group: 'Commerce',   roles: OPS_ACCT, keywords: 'gift card voucher bearer redeem balance issue void' },
  { path: '/vouchers',        label: 'Event vouchers',    group: 'Commerce',   roles: OPS_ACCT, keywords: 'event voucher gift redeem experience tasting recipient claim' },
  { path: '/customers',       label: 'Customers',         group: 'Customers',  roles: OPS_ACCT, keywords: 'users freeze unfreeze accounts profile' },
  { path: '/loyalty',         label: 'Loyalty',           group: 'Customers',  roles: OPS_ACCT, keywords: 'points tiers drops adjustments aged vintage' },
  { path: '/referrals',       label: 'Referrals',         group: 'Customers',  roles: OPS_ACCT, keywords: 'referral code invite friend bonus signup conversion drops' },
  { path: '/stores',          label: 'Stores',            group: 'Operations', roles: OPS,      keywords: 'branches locations receipt template tagline address phone tax number pos flagship' },
  { path: '/suppliers',       label: 'Suppliers',         group: 'Operations', roles: OPS,      keywords: 'erp purchase orders vendors distributors contacts wholesale' },
  { path: '/purchase-orders', label: 'Purchase Orders',   group: 'Operations', roles: OPS,      keywords: 'po erp stock intake receiving cogs wholesale receive shipment' },
  { path: '/stock-counts',    label: 'Stock Counts',      group: 'Operations', roles: OPS,      keywords: 'inventory count cycle variance shrinkage physical reconciliation erp' },
  { path: '/stock-transfers', label: 'Stock Transfers',   group: 'Operations', roles: OPS,      keywords: 'transfer yhc outlet inter warehouse move ledger stock' },
  { path: '/accounts-payable', label: 'Accounts Payable', group: 'Finance',    roles: OPS_ACCT, keywords: 'ap invoices supplier payments aging cheque bank transfer erp cash flow' },
  { path: '/pnl',             label: 'P&L',               group: 'Finance',    roles: OPS_ACCT, keywords: 'profit loss statement pnl financials revenue cogs net income accountant period close' },
  { path: '/cash-flow',       label: 'Cash Flow',         group: 'Finance',    roles: OPS_ACCT, keywords: 'cash flow inflows outflows forecast liquidity due suppliers' },
  { path: '/delivery-balance', label: 'Delivery Balance', group: 'Finance',    roles: OPS_ACCT, keywords: 'delivery fees ambassador driver earnings per km balance surplus deficit tips payout monthly' },
  { path: '/dispatch',        label: 'Dispatch',          group: 'Operations', roles: OPS,      keywords: 'assign driver tier escalation queue' },
  { path: '/live-map',        label: 'Live Map',          group: 'Operations', roles: OPS,      keywords: 'drivers location tracking real-time map' },
  { path: '/drivers',         label: 'Ambassadors',       group: 'Operations', roles: OPS,      keywords: 'drivers ambassador onboard active cash settle' },
  { path: '/issues',          label: 'Issues',            group: 'Operations', roles: OPS,      keywords: 'incidents driver report unreachable underage' },
  { path: '/support',         label: 'Support Inbox',     group: 'Operations', roles: OPS,      keywords: 'tickets help customer complaint refund' },
  { path: '/experiences',     label: 'Experiences',       group: 'Engagement', roles: OPS_MKT,  keywords: 'editorial cards discovery' },
  { path: '/events',          label: 'Events',            group: 'Engagement', roles: OPS_MKT,  keywords: 'tickets door check-in tasting masterclass' },
  { path: '/bookings',        label: 'All Bookings',      group: 'Engagement', roles: OPS_MKT,  keywords: 'tickets attendees export search filter cancelled refunded' },
  { path: '/catering',        label: 'Catering',          group: 'Engagement', roles: OPS_ACCT, keywords: 'private event party quote bartender package' },
  { path: '/games',           label: 'Games',             group: 'Engagement', roles: OPS_MKT,  keywords: 'spin wheel scratch mystery box slot lottery prizes' },
  { path: '/prizes',          label: 'Prizes',            group: 'Engagement', roles: OPS_MKT,  keywords: 'redeem in-store code physical' },
  { path: '/notifications',   label: 'Notifications',      group: 'System',     roles: OPS_MKT,  keywords: 'push broadcast send announcement' },
  { path: '/whatsapp-campaigns', label: 'WhatsApp Campaigns', group: 'System',  roles: OPS_MKT,  keywords: 'whatsapp marketing campaign blast template promo wa' },
  { path: '/marketing-site',  label: 'Marketing Site',     group: 'System',     roles: OPS_MKT,  keywords: 'cms content drinx.jo banners pages site editor' },
  { path: '/settings',        label: 'Settings',           group: 'System',     roles: OPS,      keywords: 'config dispatch fees drops tiers ai store' },
  { path: '/admin-users',     label: 'Admin Users',        group: 'System',     roles: SUP,      keywords: 'staff roles invite operations marketing accounting' },
  { path: '/permissions',     label: 'Permissions Matrix', group: 'System',     roles: SUP,      keywords: 'rbac roles matrix grid access who-can-do-what' },
  { path: '/audit-log',       label: 'Audit Log',          group: 'System',     roles: SUP,      keywords: 'history changes admin trail compliance security' },
  { path: '/data-purge',      label: 'Data Purge',         group: 'System',     roles: SUP,      keywords: 'wipe reset test cutover clean go-live launch destructive' },
  { path: '/kb',              label: 'Knowledge Base',     group: 'System',     roles: ALL,      keywords: 'docs help guide manual sop' },
  { path: '/profile',         label: 'Profile & Password', group: 'System',     roles: ALL,      keywords: 'me settings password change' },
]

// ── Icons by entity type ──────────────────────────────────────────────────
const ENTITY_ICON: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  product: Package,
  customer: Users,
  order: ShoppingCart,
  driver: Truck,
  event: CalendarDays,
  category: Tag,
}

interface EntityHit {
  type: 'product' | 'customer' | 'order' | 'driver' | 'event' | 'category'
  id: string
  title: string
  subtitle?: string
  url: string
}

export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, can } = useAuth()
  const role = user?.role
  const [query, setQuery] = useState('')

  // Close palette + reset query when route changes (navigation success).
  useEffect(() => { onOpenChange(false); setQuery('') }, [location.pathname, onOpenChange])

  // Backend entity search — only fires when the user has typed >=2 chars.
  // 200ms debounce via React Query's staleTime; the keyed-query nature means
  // typing fast cancels older requests cleanly.
  const trimmed = query.trim()
  const { data: entityData } = useQuery<{ hits: EntityHit[] }>({
    queryKey: ['palette-search', trimmed],
    queryFn: () => api.get(`/search?q=${encodeURIComponent(trimmed)}`).then(r => r.data),
    enabled: trimmed.length >= 2,
    staleTime: 200,
  })

  // KB doc search (client-side, instant) — filtered by section the role can see.
  const kbHits = useMemo<Doc[]>(() => {
    if (trimmed.length < 2) return []
    return searchDocs(trimmed)
      .filter(d => canSeeSection(d.section, role))
      .slice(0, 6)
  }, [trimmed, role])

  // Pages visible to this role.
  const visiblePages = useMemo(() => PAGES.filter(p => can(p.roles)), [can])

  const go = useCallback((url: string) => {
    onOpenChange(false)
    setQuery('')
    navigate(url)
  }, [navigate, onOpenChange])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[2000] bg-black/70 flex items-start justify-center pt-[15vh] px-4"
      onClick={() => onOpenChange(false)}
    >
      <Command
        label="Command palette"
        loop
        className="w-full max-w-2xl bg-[#161616] border border-[#2a2a2a] rounded-xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#2a2a2a]">
          <Search size={18} className="text-gray-500 flex-shrink-0" />
          <Command.Input
            placeholder="Search pages, customers, orders, products, KB…"
            className="flex-1 bg-transparent text-white placeholder-gray-500 outline-none text-[15px]"
            value={query}
            onValueChange={setQuery}
            autoFocus
          />
          <kbd className="text-[10px] text-gray-500 bg-[#0f0f0f] px-2 py-1 rounded border border-[#2a2a2a]">esc</kbd>
        </div>

        <Command.List className="max-h-[60vh] overflow-y-auto p-2">
          <Command.Empty className="px-3 py-6 text-center text-sm text-gray-500">
            {trimmed.length < 2
              ? 'Type at least 2 characters to search products, customers, orders, events, and docs.'
              : 'No matches.'}
          </Command.Empty>

          {/* Pages — always indexed against the typed query, no debounce */}
          <PaletteGroup title="Pages" icon={LayoutDashboard}>
            {visiblePages.map(p => (
              <Command.Item
                key={p.path}
                value={`${p.label} ${p.group} ${p.keywords}`}
                onSelect={() => go(p.path)}
                className="palette-row"
              >
                <span className="text-gray-400 text-[10px] w-20 truncate">{p.group}</span>
                <span className="text-white flex-1">{p.label}</span>
                <ArrowRight size={14} className="text-gray-600" />
              </Command.Item>
            ))}
          </PaletteGroup>

          {/* DB entities — only when the backend has returned hits */}
          {entityData?.hits && entityData.hits.length > 0 && (
            <PaletteGroup title="Records" icon={Package}>
              {entityData.hits.map(h => {
                const Icon = ENTITY_ICON[h.type] ?? Package
                return (
                  <Command.Item
                    key={`${h.type}-${h.id}`}
                    value={`${h.type} ${h.title} ${h.subtitle ?? ''}`}
                    onSelect={() => go(h.url)}
                    className="palette-row"
                  >
                    <Icon size={14} className="text-[#C41E3A] flex-shrink-0" />
                    <span className="text-white flex-1 truncate">{h.title}</span>
                    {h.subtitle && <span className="text-gray-500 text-xs truncate max-w-[40%]">{h.subtitle}</span>}
                    <span className="text-gray-600 text-[10px] uppercase tracking-wider w-14 text-right">{h.type}</span>
                  </Command.Item>
                )
              })}
            </PaletteGroup>
          )}

          {/* KB docs */}
          {kbHits.length > 0 && (
            <PaletteGroup title="Knowledge Base" icon={BookOpen}>
              {kbHits.map(d => (
                <Command.Item
                  key={d.slug}
                  value={`kb ${d.frontmatter.title} ${d.section} ${d.frontmatter.tags.join(' ')}`}
                  onSelect={() => go(`/kb/${d.slug}`)}
                  className="palette-row"
                >
                  <BookOpen size={14} className="text-[#C41E3A] flex-shrink-0" />
                  <span className="text-white flex-1 truncate">{d.frontmatter.title}</span>
                  <span className="text-gray-600 text-[10px] uppercase tracking-wider w-14 text-right">{d.section}</span>
                </Command.Item>
              ))}
            </PaletteGroup>
          )}
        </Command.List>

        <div className="px-4 py-2 border-t border-[#2a2a2a] text-[10px] text-gray-500 flex items-center justify-between bg-[#0f0f0f]">
          <span>↑↓ navigate · ↵ open · esc close</span>
          <span>Cmd/Ctrl+K to reopen</span>
        </div>
      </Command>
    </div>
  )
}

// ── Helper subcomponents ──────────────────────────────────────────────────
function PaletteGroup({
  title,
  icon: Icon,
  children,
}: {
  title: string
  icon: React.ComponentType<{ size?: number; className?: string }>
  children: React.ReactNode
}) {
  return (
    <Command.Group
      heading={
        <div className="flex items-center gap-2 px-2 py-1 text-[10px] uppercase tracking-widest text-gray-500">
          <Icon size={11} />
          {title}
        </div>
      }
    >
      {children}
    </Command.Group>
  )
}
